import { useSavedSearchStore } from '@/stores/filters';
import { useSavedSearchUtils } from '@/composables/useSavedSearchUtils';
import { useTutorialsPanelStore } from '@/stores/useTutorialsPanelStore';
import { useUserDetailsStore } from '@/stores/useUserDetails';
import { walkthroughSetupIds } from '@/constants/featureAnnouncements';
import { onboardingWalkthroughSetupIds } from '@/constants/onboardingChecklist';

import useAcvDatadog from '@/composables/useAcvDatadog';
import { storeToRefs } from 'pinia';
/**
 * Opens whatever UI a walkthrough's steps need to be pointing at, before it starts, and
 * (optionally) reverses that when the carrier navigates back past the step that ran it — see
 * `back()` in `useWalkthroughController.js`.
 *
 * The Saved Searches walkthrough is the example: one of its steps highlights "Create a Saved
 * Search", a button that only exists while the sidebar is open. So that step declares
 * `setup: walkthroughSetupIds.openSavedSearchSidebar`, and the `run` function below opens the
 * sidebar as the walkthrough reaches it. Without it the step would simply be skipped, because a
 * walkthrough only shows steps whose targets are visible when it reaches them.
 *
 * Steps name a key rather than holding the function, so that `constants/featureAnnouncements.js`
 * / `constants/onboardingChecklist.js` stay plain data — no store imports, no import cycles,
 * still serializable if this content ever comes from an API.
 *
 * `teardown` is optional and only worth writing where reversing `run` has real consequences —
 * most setups (opening a menu, opening a sidebar) are harmless to leave in place or to simply
 * re-run, so `back()` falls back to replaying forward rather than every setup needing a paired
 * teardown.
 *
 * Grouped by tutorial below, each an IIFE returning its slice of the registry — that gives a
 * tutorial's setups a shared local scope (helpers, captured state) without leaking into any
 * other tutorial's, and without the runtime lookup (`runWalkthroughSetup`/`runWalkthroughTeardown`
 * below) needing to know "tutorial" is a concept at all: the groups are flattened into one
 * ordinary `setupId -> entry` map before either of those ever runs.
 */
const featureAnnouncementSetups = {
  [walkthroughSetupIds.openSavedSearchSidebar]: {
    run: () => {
      const savedSearchStore = useSavedSearchStore();
      const { isSavedSearchSideBarOpen } = storeToRefs(savedSearchStore);
      savedSearchStore.getSavedSearchesForCarrier();
      isSavedSearchSideBarOpen.value = true;
    },
  },
};

const tmsWalkthroughSetups = {
  [onboardingWalkthroughSetupIds.openAccountMenu]: {
    run: () => {
      useTutorialsPanelStore().openAccountMenu();
    },
    teardown: () => {
      useTutorialsPanelStore().closeAccountMenu();
    },
  },
  [onboardingWalkthroughSetupIds.closeAndOpenAccountMenu]: {
    run: () => {
      useTutorialsPanelStore().closeAccountMenu();
    },
    teardown: () => {
      useTutorialsPanelStore().openAccountMenu();
    },
  },
  [onboardingWalkthroughSetupIds.openTmsSidebar]: {
    run: () => {
      const { isTmsSheetOpen } = storeToRefs(useUserDetailsStore());
      isTmsSheetOpen.value = true;
    },
    teardown: () => {
      const { isTmsSheetOpen } = storeToRefs(useUserDetailsStore());
      isTmsSheetOpen.value = false;
    },
  },
};

const fleetWalkthroughSetups = {
  [onboardingWalkthroughSetupIds.openEditVehicleInfo]: {
    run: () => {
      // TODO: open the Edit Vehicle Info side menu
    },
  },
};

const savedSearchWalkthroughSetups = (() => {
  const ensureCreateFormOpen = () => {
    const { isCreateFormOpen } = storeToRefs(useSavedSearchStore());
    if (!isCreateFormOpen.value) {
      useSavedSearchUtils().createFormToggle();
    }
  };

  const ensureCreateFormClosed = () => {
    const { isCreateFormOpen } = storeToRefs(useSavedSearchStore());
    if (isCreateFormOpen.value) {
      useSavedSearchUtils().createFormToggle();
    }
  };

  return {
    [onboardingWalkthroughSetupIds.openSavedSearchSidebar]: {
      run: () => {
        const savedSearchStore = useSavedSearchStore();
        const { isSavedSearchSideBarOpen } = storeToRefs(savedSearchStore);
        savedSearchStore.getSavedSearchesForCarrier();
        isSavedSearchSideBarOpen.value = true;
      },
    },
    [onboardingWalkthroughSetupIds.openCreateSavedSearch]: {
      run: ensureCreateFormOpen,
      teardown: ensureCreateFormClosed,
    },
    [onboardingWalkthroughSetupIds.closeAndOpenSavedSearch]: {
      run: ensureCreateFormClosed,
      teardown: ensureCreateFormOpen,
    },
  };
})();

const instantBookWalkthroughSetups = {
  [onboardingWalkthroughSetupIds.findInstantBookableJob]: {
    run: () => {
      // TODO: find instant bookable job
    },
  },
  [onboardingWalkthroughSetupIds.openJobCard]: {
    run: () => {
      // TODO: open the job card modal
    },
  },
};

const walkthroughSetupRegistry = {
  ...featureAnnouncementSetups,
  ...tmsWalkthroughSetups,
  ...fleetWalkthroughSetups,
  ...savedSearchWalkthroughSetups,
  ...instantBookWalkthroughSetups,
};

/**
 * Runs the named setup's `run`, if there is one. An unknown key is reported and otherwise
 * ignored: the walkthrough still runs, showing whichever steps happen to be visible, rather
 * than failing outright over stale content.
 */
export const runWalkthroughSetup = (setupId) => {
  if (!setupId) {
    return;
  }

  const entry = walkthroughSetupRegistry[setupId];

  if (!entry) {
    useAcvDatadog().addRumError('Unknown walkthrough setup id', { setupId });
    return;
  }

  entry.run();
};

/**
 * Runs the named setup's `teardown`, if one is declared. Returns whether it ran, so a caller
 * (`back()`) can fall back to its own recovery when there's nothing to run — an unknown setup
 * id and a setup with no teardown both simply report "nothing happened" rather than throwing.
 */
export const runWalkthroughTeardown = (setupId) => {
  if (!setupId) {
    return false;
  }

  const entry = walkthroughSetupRegistry[setupId];

  if (!entry?.teardown) {
    return false;
  }

  entry.teardown();
  return true;
};
